import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import '../../styles/enterprise.css';

/**
 * "Showing a-b of total" + numbered pager (active = black square) + chevrons.
 *
 * <Pager page={1} pageCount={5} total={47} pageSize={10} onPage={setPage} />
 *
 * `page` is 1-based. Renders a compact window of pages with ellipses.
 */
const buildPages = (page, pageCount) => {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const set = new Set([1, 2, pageCount - 1, pageCount, page - 1, page, page + 1]);
  const list = [...set].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of list) {
    if (prev && p - prev > 1) out.push('…');
    out.push(p);
    prev = p;
  }
  return out;
};

const Pager = ({ page, pageCount, total, pageSize, onPage }) => {
  const safeCount = Math.max(1, pageCount || 1);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const pages = buildPages(page, safeCount);

  const go = (p) => {
    if (p < 1 || p > safeCount || p === page) return;
    onPage && onPage(p);
  };

  return (
    <div className="pager">
      <span className="pager__info">
        Showing {from}-{to} of {total} {total === 1 ? 'result' : 'results'}
      </span>
      <div className="pager__pages">
        <button
          type="button"
          className="pager__btn"
          onClick={() => go(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft size={15} />
        </button>
        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`e${i}`} className="pager__ellipsis">…</span>
          ) : (
            <button
              key={p}
              type="button"
              className={`pager__btn ${p === page ? 'active' : ''}`.trim()}
              onClick={() => go(p)}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          className="pager__btn"
          onClick={() => go(page + 1)}
          disabled={page >= safeCount}
          aria-label="Next page"
        >
          <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
};

export default Pager;
